import { Breadcrumbs, Link, Typography, Box } from '@mui/material';
import { NavigateNext as NavigateNextIcon, Home as HomeIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

interface BreadcrumbItem {
    label: string;
    path?: string;
}

interface BreadcrumbNavigationProps {
    items?: BreadcrumbItem[];
    inAppBar?: boolean;
}

// Route-based breadcrumb generation
const generateBreadcrumbs = (pathname: string): BreadcrumbItem[] => {
    const paths = pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [{ label: 'Dashboard', path: '/dashboard' }];

    // Map routes to readable names
    const routeLabels: Record<string, string> = {
        campaigns: 'Campaigns',
        screens: 'Screens',
        bookings: 'Bookings',
        analytics: 'Analytics',
        money: 'Money',
        creatives: 'Creatives',
        new: 'Create New',
        edit: 'Edit',
        admin: 'Admin',
        'player-fleet': 'Player Fleet',
        'player-releases': 'Player Releases',
        'live-view': 'Live View Limits',
        help: 'Help',
        'install-player': 'Install a player',
    };

    let currentPath = '';
    paths.forEach((path, index) => {
        currentPath += `/${path}`;

        // Skip certain paths like 'new' or 'edit' if not last
        if ((path === 'new' || path === 'edit') && index < paths.length - 1) {
            return;
        }

        // If it's a GUID/ID, show as "Details"
        const isId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(path);

        breadcrumbs.push({
            label: isId ? 'Details' : (routeLabels[path] || path),
            path: index === paths.length - 1 ? undefined : currentPath,
        });
    });

    return breadcrumbs;
};

export default function BreadcrumbNavigation({ items, inAppBar = false }: BreadcrumbNavigationProps) {
    const navigate = useNavigate();
    const location = useLocation();

    const breadcrumbs = items || generateBreadcrumbs(location.pathname);

    if (breadcrumbs.length <= 1) {
        return null; // Don't show breadcrumbs if only on dashboard
    }

    return (
        <Box mb={inAppBar ? 0 : 3}>
            <Breadcrumbs
                separator={
                    <NavigateNextIcon 
                        sx={{ 
                            fontSize: '14px', 
                            color: inAppBar ? '#333333' : 'inherit' 
                        }} 
                    />
                }
                aria-label="breadcrumb"
            >
                {breadcrumbs.map((crumb, index) => {
                    const isLast = index === breadcrumbs.length - 1;

                    if (isLast || !crumb.path) {
                        return (
                            <Typography
                                key={index}
                                color={inAppBar ? '#f0f0f0' : 'text.primary'}
                                fontWeight={inAppBar ? 400 : 'medium'}
                                sx={{ fontSize: inAppBar ? '12px' : 'inherit' }}
                            >
                                {crumb.label}
                            </Typography>
                        );
                    }

                    return (
                        <Link
                            key={index}
                            underline="hover"
                            color="inherit"
                            onClick={() => navigate(crumb.path!)}
                            sx={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                fontSize: inAppBar ? '12px' : 'inherit',
                                color: inAppBar ? '#888888 !important' : 'inherit',
                                '&:hover': {
                                    color: inAppBar ? '#f0f0f0 !important' : 'inherit'
                                }
                            }}
                        >
                            {index === 0 && <HomeIcon sx={{ fontSize: inAppBar ? '14px' : 'small' }} />}
                            {crumb.label}
                        </Link>
                    );
                })}
            </Breadcrumbs>
        </Box>
    );
}
